import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { PaperPlaneTilt, BellRinging, Link as LinkIcon, Eye } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { API, L, REQ_STATUS, Badge, fmtDate } from './shared';
import { InvitePreviewDialog } from './InvitePreviewDialog';
import { useAuth } from '../../context/AuthContext';

const OwnerRow = ({ o, checked, onToggle, canSend, lang, onRemind, onCopy, busy }) => {
  const r = o.latest_request;
  return (
    <tr className="border-t border-zinc-100" data-testid={`of-owner-row-${o.apartment_id}`}>
      <td className="px-2 py-1.5"><input type="checkbox" disabled={!o.has_email || !canSend} checked={checked} onChange={onToggle} data-testid={`of-owner-check-${o.apartment_id}`} /></td>
      <td className="px-2 py-1.5 font-mono">{o.apartment_number}</td>
      <td className="px-2 py-1.5">{o.owner_name || '—'}</td>
      <td className="px-2 py-1.5 text-zinc-600">{o.email || <span className="text-red-600 text-xs">{L(lang, 'Χωρίς email', 'No email')}</span>}</td>
      <td className="px-2 py-1.5">{r ? <Badge map={REQ_STATUS} value={r.status} lang={lang} testid={`of-owner-status-${o.apartment_id}`} /> : <span className="text-xs text-zinc-400">{L(lang, 'Δεν προσκλήθηκε', 'Not invited')}</span>}</td>
      <td className="px-2 py-1.5 text-xs text-zinc-500">{r ? `${fmtDate(r.invited_at)}${r.reminders?.length ? ` · ${r.reminders.length} ${L(lang, 'υπενθ.', 'remind.')}` : ''}` : ''}{r?.email_status && r.email_status.startsWith('failed') && <div className="text-red-600" title={r.email_status}>{L(lang, 'Αποτυχία email', 'Email failed')}</div>}</td>
      <td className="px-2 py-1.5 text-xs font-mono text-center">{o.responses_count}</td>
      <td className="px-2 py-1.5 text-right whitespace-nowrap">
        {canSend && r?.status === 'pending' && (<>
          <Button size="sm" variant="outline" className="h-7 rounded-none mr-1" disabled={busy} onClick={() => onRemind(r)} data-testid={`of-remind-${o.apartment_id}`}><BellRinging size={14} className="mr-1" />{L(lang, 'Επαναποστολή', 'Resend')}</Button>
          <Button size="sm" variant="ghost" className="h-7 rounded-none" onClick={() => onCopy(r)} title={L(lang, 'Αντιγραφή προσωπικού συνδέσμου', 'Copy personal link')} data-testid={`of-copy-link-${o.apartment_id}`}><LinkIcon size={14} /></Button>
        </>)}
      </td>
    </tr>
  );
};

export const OwnerFeedbackOwners = ({ buildingId, owners, reload, canSend, lang }) => {
  const [sel, setSel] = useState(new Set());
  const [formLang, setFormLang] = useState(lang === 'en' ? 'en' : 'el');
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(false);
  const { user } = useAuth();
  const eligible = useMemo(() => owners.filter((o) => o.has_email && o.latest_request?.status !== 'pending'), [owners]);
  const toggle = (id) => setSel((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const allOn = eligible.length > 0 && eligible.every((o) => sel.has(o.apartment_id));

  const invite = async () => {
    setBusy(true);
    try {
      const r = (await axios.post(`${API}/buildings/${buildingId}/owner-feedback/invite`, { apartment_ids: [...sel], lang: formLang, confirm: true })).data;
      const skipped = r.results.filter((x) => x.result === 'skipped');
      toast.success(L(lang, `Στάλθηκαν ${r.invited} ατομικές προσκλήσεις`, `${r.invited} individual invitations sent`));
      if (skipped.length) toast.warning(L(lang, `${skipped.length} παραλείφθηκαν (χωρίς email ή με εκκρεμή πρόσκληση)`, `${skipped.length} skipped (no email or pending invitation)`));
      setSel(new Set()); setPreview(false); reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); } finally { setBusy(false); }
  };
  const remind = async (r) => {
    setBusy(true);
    try {
      const res = (await axios.post(`${API}/owner-feedback/requests/${r.request_id}/remind`)).data;
      if (res.email_status.startsWith('failed')) toast.error(res.email_status); else toast.success(L(lang, 'Η πρόσκληση στάλθηκε ξανά', 'Invitation resent'));
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); } finally { setBusy(false); }
  };
  const copy = async (r) => {
    try {
      const { link } = (await axios.get(`${API}/owner-feedback/requests/${r.request_id}/link`)).data;
      await navigator.clipboard.writeText(link);
      toast.success(L(lang, 'Ο προσωπικός σύνδεσμος αντιγράφηκε — μοιραστείτε τον μόνο με αυτόν τον ιδιοκτήτη', 'Personal link copied — share it only with this owner'));
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  return (
    <div data-testid="of-owners-panel">
      {canSend && (
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={allOn} onChange={() => setSel(allOn ? new Set() : new Set(eligible.map((o) => o.apartment_id)))} data-testid="of-select-all" /> {L(lang, 'Επιλογή όλων', 'Select all')} ({eligible.length})</label>
          <select className="border border-zinc-300 text-xs px-2 h-8" value={formLang} onChange={(e) => setFormLang(e.target.value)} data-testid="of-invite-lang">
            <option value="el">Ελληνικά</option><option value="en">English</option>
          </select>
          <Button variant="outline" className="rounded-none h-8" onClick={() => setPreview(true)} data-testid="of-preview-btn"><Eye size={14} className="mr-1" />{L(lang, 'Προεπισκόπηση & Δοκιμή', 'Preview & Test')}</Button>
          <Button className="rounded-none bg-zinc-900 h-8" disabled={busy || !sel.size} onClick={() => setPreview(true)} data-testid="of-send-invites-btn"><PaperPlaneTilt size={14} className="mr-1" />{L(lang, `Αποστολή ${sel.size} ${sel.size === 1 ? 'πρόσκλησης' : 'προσκλήσεων'}`, `Send ${sel.size} ${sel.size === 1 ? 'invitation' : 'invitations'}`)}</Button>
          <span className="text-[11px] text-zinc-500">{L(lang, 'Κάθε ιδιοκτήτης λαμβάνει ξεχωριστό email με δικό του ασφαλή σύνδεσμο (ισχύς 60 ημέρες, μία υποβολή). Δεν στέλνεται τίποτα αυτόματα.', 'Each owner gets a separate email with their own secure link (valid 60 days, single use). Nothing is sent automatically.')}</span>
        </div>
      )}
      <div className="border border-zinc-200 bg-white overflow-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900 text-white text-[10px] uppercase tracking-wider"><tr>
            <th className="w-8" />{[L(lang, 'Διαμ.', 'Apt'), L(lang, 'Ιδιοκτήτης', 'Owner'), 'Email', L(lang, 'Κατάσταση', 'Status'), L(lang, 'Πρόσκληση', 'Invited'), L(lang, 'Απαντήσεις', 'Responses'), ''].map((h, i) => <th key={i} className="text-left px-2 py-1.5">{h}</th>)}
          </tr></thead>
          <tbody>{owners.map((o) => <OwnerRow key={o.apartment_id} o={o} lang={lang} canSend={canSend} busy={busy} checked={sel.has(o.apartment_id)} onToggle={() => toggle(o.apartment_id)} onRemind={remind} onCopy={copy} />)}</tbody>
        </table>
        {!owners.length && <div className="p-6 text-center text-sm text-zinc-400">{L(lang, 'Δεν υπάρχουν διαμερίσματα', 'No apartments')}</div>}
      </div>
      <InvitePreviewDialog open={preview} onClose={() => setPreview(false)} buildingId={buildingId} formLang={formLang} lang={lang}
        apartmentId={[...sel][0] || owners[0]?.apartment_id} count={sel.size} onConfirm={invite} adminEmail={user?.email} />
    </div>
  );
};

export default OwnerFeedbackOwners;
